import TextField from '@base/TextField/TextField';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import Button from 'aquilla/core/molecules/Button';
import { segment } from '@actions/analytics';
import { CHECKOUT_CBT_USER_DETAILS_SUBMITTED } from '@cont/Checkout/constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getFullName } from '@api/selectors/auth';
import { fetchCbtCentres, fetchCbtLocations } from '@api/fetchers/cbt';
import { useEffect, useState } from 'react';
import useTestSeriesInfo from './useTestSeriesInfo';
import CbtCentreSelect from './CbtCentreSelect';
import CbtSearchableSelect from './CbtSearchableSelect';
import useCbtVenueDropdown from './useCbtVenueDropdown';
import MapsLink from './MapsLink';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_2B};
  width: 100%;
`;

const FieldLabel = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H6};
  line-height: 18px;
  color: var(--color-text-primary);

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.ULTRA_SMALL};
    line-height: ${Spacings.SPACING_4B};
  }
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacings.SPACING_2B};
`;

const HelperText = styled(Typography)`
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Spacings.SPACING_4B};
  color: var(--color-text-secondary);
`;

const StyledTextField = styled(TextField)`
  width: 100%;
  border-radius: ${Spacings.SPACING_2B};
`;

const ProceedButton = styled(Button)`
  min-width: 251px;
  height: 48px;
  border: none;
  &:disabled {
    background-color: var(--color-base-light-0);
    color: var(--color-text-secondary);
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    min-width: unset;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;

  ${mediaQuery.uptoMobile} {
    justify-content: stretch;
    position: sticky;
    bottom: 0;
    background-color: var(--color-base-1);
    padding-top: ${Spacings.SPACING_4B};
    margin-top: auto;
  }
`;

const CbtVenueSelectionForm = ({
  programmeUid,
  onProceed,
  onVenueChange,
  getSegmentProps,
  isMobile = false,
  hideProceedButton = false
}) => {
  const meData = useMyInfo(getFullName);
  const { data: testSeriesData } = useTestSeriesInfo();
  const [fullName, setFullName] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  const resolvedProgrammeUid = programmeUid ?? testSeriesData?.uid;

  const [locations, setLocations] = useState({});
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [isLocationsError, setIsLocationsError] = useState(false);

  const [centres, setCentres] = useState([]);
  const [isCentresLoading, setIsCentresLoading] = useState(false);
  const [isCentresError, setIsCentresError] = useState(false);

  useEffect(() => {
    if (!resolvedProgrammeUid) return undefined;

    let cancelled = false;

    setIsLocationsLoading(true);
    setIsLocationsError(false);
    setLocations({});

    fetchCbtLocations(resolvedProgrammeUid)
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setIsLocationsError(true);
          return;
        }
        setLocations(response?.locations ?? {});
      })
      .catch(() => {
        if (cancelled) return;
        setIsLocationsError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLocationsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedProgrammeUid]);

  useEffect(() => {
    if (meData) {
      setFullName(meData);
    }
  }, [meData]);

  const {
    stateOptions,
    cityOptions,
    centerOptions,
    selectedState,
    selectedCity,
    selectedCenter,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  } = useCbtVenueDropdown({ locations, centres });

  useEffect(() => {
    if (!resolvedProgrammeUid || !selectedCity?.value) {
      setCentres([]);
      return undefined;
    }

    let cancelled = false;
    const city = selectedCity.value;

    setIsCentresLoading(true);
    setIsCentresError(false);
    setCentres([]);

    fetchCbtCentres(resolvedProgrammeUid, city)
      .then((response) => {
        if (cancelled) return;
        if (response?.error) {
          setIsCentresError(true);
          return;
        }
        setCentres(response?.centres ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setIsCentresError(true);
      })
      .finally(() => {
        if (!cancelled) setIsCentresLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [resolvedProgrammeUid, selectedCity?.value]);

  const selectedCentreData = selectedCenter
    ? centres.find((centre) => centre.centreUid === selectedCenter.value)
    : null;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const canProceed =
    fullName.trim().length > 0 &&
    selectedState &&
    selectedCity &&
    selectedCenter &&
    selectedCentreData?.availableSeats > 0 &&
    !isCentresLoading;

  useEffect(() => {
    if (!onVenueChange) return;
    onVenueChange(
      canProceed && selectedCentreData
        ? {
            fullName: fullName.trim(),
            state: selectedState.value,
            city: selectedCity.value,
            centreUid: selectedCentreData.centreUid,
            centreName: selectedCentreData.centreName,
            centreAddress: selectedCentreData.centreAddress
          }
        : null
    );
  }, [
    canProceed,
    fullName,
    onVenueChange,
    selectedCentreData,
    selectedCity,
    selectedState
  ]);

  const handleProceed = async () => {
    if (!canProceed || !selectedCentreData || isSubmitting) return;

    const venueSelection = {
      fullName: fullName.trim(),
      state: selectedState.value,
      city: selectedCity.value,
      centreUid: selectedCentreData.centreUid,
      centreName: selectedCentreData.centreName,
      centreAddress: selectedCentreData.centreAddress
    };

    if (getSegmentProps) {
      segment.track(
        CHECKOUT_CBT_USER_DETAILS_SUBMITTED,
        getSegmentProps(venueSelection),
        evtNameCategoryMap.BOTH
      );
    }

    setIsSubmitting(true);
    try {
      await onProceed(venueSelection);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormWrapper>
      <FieldGroup>
        <FieldLabel variant="p3">Full name</FieldLabel>
        <StyledTextField
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Enter your full name"
          width="100%"
        />
        <HelperText variant="p3">
          Enter your full legal name as per your Aadhar card
        </HelperText>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel variant="p3">Choice of state</FieldLabel>
        <CbtSearchableSelect
          options={stateOptions}
          selectedOption={selectedState}
          onSelect={setSelectedState}
          placeholder="Search for state"
          disabled={isLocationsLoading || !stateOptions.length}
          isOpen={openDropdown === 'state'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'state' : null)}
        />
        {isLocationsError && (
          <HelperText variant="p3">
            Unable to load locations. Please go back and try again.
          </HelperText>
        )}
        {!isLocationsLoading && !isLocationsError && !stateOptions.length && (
          <HelperText variant="p3">
            No locations available for this test series.
          </HelperText>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabel variant="p3">Choice of city</FieldLabel>
        <CbtSearchableSelect
          options={cityOptions}
          selectedOption={selectedCity}
          onSelect={setSelectedCity}
          placeholder="Search for city"
          disabled={!selectedState}
          isOpen={openDropdown === 'city'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'city' : null)}
        />
      </FieldGroup>

      <FieldGroup>
        <FieldLabelRow>
          <FieldLabel variant="p3">Choice of centre</FieldLabel>
          <MapsLink
            href={selectedCentreData?.centreGoogleMapsLink}
            marginTop="0"
            showDisabled
          />
        </FieldLabelRow>
        <CbtCentreSelect
          options={centerOptions}
          selectedOption={selectedCenter}
          onSelect={setSelectedCenter}
          disabled={!selectedCity || isCentresLoading}
          isOpen={openDropdown === 'centre'}
          onToggle={(isOpen) => setOpenDropdown(isOpen ? 'centre' : null)}
        />
        {isCentresError && (
          <HelperText variant="p3">
            Unable to load centres. Please try another city.
          </HelperText>
        )}
      </FieldGroup>

      {!hideProceedButton && (
        <ActionRow>
          <ProceedButton
            label="Proceed to buy test series"
            variant="primary"
            size={isMobile ? 'large' : 'medium'}
            disabled={!canProceed || isSubmitting}
            onClick={handleProceed}
          />
        </ActionRow>
      )}
    </FormWrapper>
  );
};

export default CbtVenueSelectionForm;
